/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */
import './NewUserFlow.scss';

import noop from 'lodash/noop';
import { func } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import BaseModal from 'components/shared/Modal/BaseModal';
import {
  isValidFirstLastName,
  isValidTaxId,
  convertValidationToBoolean
} from 'services/Validation';
import Store from 'state/Store';
import {
  clearTouchedFields,
  touchedField
} from 'state/validation/ValidationActions';
import { clearGrantViewAccessAddUserAuth } from 'state/grantViewAccess/GrantViewAccessActions';
import NewUserForm from './NewUserForm/NewUserForm';
import SelectViewableAccounts from './SelectViewableAccounts/SelectViewableAccounts';
import Content from 'services/Content';
import { getGrantViewAccessAddUserAuth } from 'services/Rest';
import {
  isGrantViewAccessAddUserAuthorized,
  isGrantViewAccessAddUserRequested
} from 'state/grantViewAccess/GrantViewAccessSelectors';

const stepTitles = {
  1: 'Provide Access to a User',
  2: 'View-Only Access By Investment'
};

class AddNewUserFlow extends React.Component {
  state = {
    currStep: 1,
    firstName: '',
    lastName: '',
    ssn: '',
    checkedItems: {}
  };

  componentDidUpdate = (prevProps) => {
    if (
      this.props.addUserAuthRequested !== prevProps.addUserAuthRequested ||
      this.props.addUserAuthorized !== prevProps.addUserAuthorized
    ) {
      this.updateModalState();
    }
  };

  updateFormField = (field) => (eventOrValue) => {
    Store.dispatch(touchedField(field));
    this.setState({
      [field]: eventOrValue.target ? eventOrValue.target.value : eventOrValue
    });
  };

  checkIfShouldDisable = () => {
    const { currStep, firstName, lastName, ssn, scrolledThroughDisclosure } =
      this.state;

    const hasInfo = firstName && lastName && ssn;
    const isValidInfo =
      convertValidationToBoolean(isValidFirstLastName(firstName)) &&
      convertValidationToBoolean(isValidFirstLastName(lastName)) &&
      convertValidationToBoolean(isValidTaxId(ssn));

    return currStep === 1
      ? !hasInfo || !(isValidInfo && scrolledThroughDisclosure)
      : !Object.values(this.state.checkedItems).some((v) => v);
  };

  onContinue = () => {
    const { firstName, lastName, ssn } = this.state;

    const requestBody = {
      firstName,
      lastName,
      taxId: ssn
    };

    getGrantViewAccessAddUserAuth(requestBody);
    Store.dispatch(clearTouchedFields());
  };

  updateModalState = () => {
    const { addUserAuthRequested, addUserAuthorized } = this.props;

    if (addUserAuthRequested && addUserAuthorized) {
      this.setState({ currStep: this.state.currStep + 1 });
      Store.dispatch(clearGrantViewAccessAddUserAuth());
    }
  };

  onBack = () => {
    Store.dispatch(clearTouchedFields());
    this.setState({ currStep: this.state.currStep - 1 });
  };

  render() {
    const { onClose, onSave } = this.props;
    const { currStep, firstName, lastName, ssn: taxId } = this.state;

    const shouldDisable = this.checkIfShouldDisable();
    const modalTitle = stepTitles[currStep];

    const { addUserAuthRequested, addUserAuthorized } = this.props;
    const userUnauthorized = addUserAuthRequested && !addUserAuthorized;

    const grantee = {
      firstName,
      lastName,
      taxId
    };

    return (
      <BaseModal
        containerClassName="add-new-user-flow-modal"
        disableClickOff={true}
        hideAnchor={true}
        hideClose={true}
        isOpen={true}
        title={modalTitle}>
        {currStep === 1 ? (
          <NewUserForm
            onClose={onClose}
            errorMsgText={userUnauthorized ? Content.authDeniedMsg : ''}
            onContinue={this.onContinue}
            shouldDisableNext={shouldDisable}
            updateFirstName={this.updateFormField('firstName')}
            updateLastName={this.updateFormField('lastName')}
            updateScrollDetection={this.updateFormField(
              'scrolledThroughDisclosure'
            )}
            updateSSN={this.updateFormField('ssn')}
            {...this.state}
          />
        ) : (
          <SelectViewableAccounts
            checkedItems={this.state.checkedItems}
            onClose={onClose}
            grantee={grantee}
            onBack={this.onBack}
            onSave={onSave}
            shouldDisableSave={shouldDisable}
            updateCheckedItems={this.updateFormField('checkedItems')}
          />
        )}
      </BaseModal>
    );
  }
}

AddNewUserFlow.propTypes = {
  onClose: func.required,
  onSave: func.required
};

AddNewUserFlow.defaultProps = {
  onClose: noop,
  onSave: noop
};

const mapStateToProps = (state) => ({
  addUserAuthorized: isGrantViewAccessAddUserAuthorized(state),
  addUserAuthRequested: isGrantViewAccessAddUserRequested(state)
});

export default connect(mapStateToProps)(AddNewUserFlow);
